/**
 * ColumnsInCanvas — renderuje element `type='columns'` w canvasie kreatora jako
 * PRAWDZIWY kontener z drop targetem (parytet z `AccordionInCanvas` / `StepperInCanvas`).
 *
 * Wcześniej `columns` leciał do generycznej gałęzi non-field w `SortableSectionView`
 * i renderował się jako statyczny podgląd (`LayoutElementBlock`) BEZ `useDroppable` —
 * przez co pole/element upuszczone „na kolumnę" trafiało do sekcji-rodzica zamiast do
 * wnętrza kontenera. Ten komponent rejestruje własny drop target (`useDroppable`
 * z `data.target='section'` + `sectionId=element.id`, kontrakt z `usePaletteDnD`),
 * dzięki czemu dispatcher wstawia dziecko do `columns.children` przez `insertIntoContainer`.
 *
 * Model danych: `LayoutElement.children: LayoutElement[]` układane w siatkę N-kolumnową
 * (settings.columns 1-6). Dzieci wypełniają komórki siatki po kolei — 1:1 z runtime
 * rendererem `ColumnsElement` (F12.H) oraz wzorcem SellCall (jeden droppable per kontener,
 * patrz `docs/sellcall/01-layout-builder.md §6`).
 */

import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useDroppable } from '@dnd-kit/core';
import { rectSortingStrategy, SortableContext } from '@dnd-kit/sortable';

import type { LayoutElement } from '@/lib/layout-tree';

const COLS_CLASS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
  5: 'grid-cols-5',
  6: 'grid-cols-6',
};

const GAP_CLASS: Record<string, string> = {
  sm: 'gap-2',
  md: 'gap-3',
  lg: 'gap-4',
};

export interface ColumnsInCanvasProps {
  element: LayoutElement;
  /**
   * Renderuje pojedyncze dziecko kolumny — parent dispatch'uje (field / nested element).
   * `containerId` to id elementu `columns`, używane przez parent do wpięcia `useSortable`
   * z `sortableContainerId={containerId}` (spójne z akordeonem / stepperem).
   */
  renderChild: (child: LayoutElement, containerId: string) => ReactNode;
}

export function ColumnsInCanvas({ element, renderChild }: ColumnsInCanvasProps): ReactNode {
  const { t } = useTranslation();
  const settings = (element.settings ?? {}) as { columns?: number; gap?: 'sm' | 'md' | 'lg' };
  const n = Math.max(1, Math.min(6, settings.columns ?? 2));
  const colsCls = COLS_CLASS[n] ?? 'grid-cols-2';
  const gapCls = GAP_CLASS[settings.gap ?? 'md'] ?? 'gap-3';
  const children = element.children ?? [];

  // Kontrakt z usePaletteDnD: kontener jest drop targetem z tym samym shape co sekcja
  // (`target='section'` + `sectionId=element.id`). Dispatcher rozpoznaje generic-container.
  const { setNodeRef, isOver } = useDroppable({
    id: `${element.id}-drop`,
    data: { source: 'section', target: 'section', sectionId: element.id },
  });

  return (
    <div
      ref={setNodeRef}
      data-testid={`columns-canvas-${element.id}`}
      className={`grid ${colsCls} ${gapCls} rounded p-1 transition ${
        isOver
          ? 'bg-accent-50/50 ring-2 ring-dashed ring-accent-400 dark:bg-accent-900/20'
          : 'ring-2 ring-transparent'
      }`}
    >
      {children.length > 0 ? (
        <SortableContext items={children.map((c) => c.id)} strategy={rectSortingStrategy}>
          {children.map((child) => (
            <div key={child.id}>{renderChild(child, element.id)}</div>
          ))}
        </SortableContext>
      ) : (
        Array.from({ length: n }).map((_, idx) => (
          <div
            key={idx}
            className="rounded border-2 border-dashed border-border-primary py-6 text-center text-xs text-text-muted"
            data-testid={`columns-canvas-${element.id}-placeholder-${idx}`}
          >
            {t('tenant.systemModuleBuilder.columns.columnLabel', { index: idx + 1 })}
          </div>
        ))
      )}
    </div>
  );
}
